import { useCallback, useMemo } from 'react';
import {
  keepPreviousData,
  useInfiniteQuery,
  useQueryClient,
  type InfiniteData,
} from '@tanstack/react-query';
import { useDebounceValue } from 'usehooks-ts';
import {
  SignalOriginSchema,
  SignalStatus,
  type BlockedReasonType,
  type ConfirmedVulnerabilityFindingStateType,
  type SignalCategoryType,
  type SignalOriginType,
  type SignalStageType,
  type SignalStatusType,
} from '@terra/shared-schemas-orchestrator';
import type {
  BaseAppSignalDto,
  ChainingGroupedSignal,
  GroupedSignal,
  NonChainingGroupedSignal,
  SignalListGroupByField,
  SignalListGroupCount,
  SignalsSearchQueryDto,
} from '@terra/shared-contracts-signals';
import { terraProxyApiClient } from '../clients/testerClient';
import { buildSignalsRequestFilters } from './signals-request-filters';
import type {
  AiComponentTypeType,
  TargetTypeType,
} from '@terra/shared-schemas-orchestrator';
import {
  PaginationDefaults,
  type LifecycleStateFilterDto,
} from '@terra/shared-dto-types';

export { mapSurfaceSignalToGroupedSignal } from '../clients/tester-client/surface-signal-mapper';

const mergeGroupedSignalUpdate = (
  signal: GroupedSignal,
  updates: Partial<BaseAppSignalDto>,
): GroupedSignal => {
  const { origin: _ignoredOrigin, ...allowedUpdates } = updates;
  const normalizedUpdates =
    allowedUpdates.status !== undefined &&
    allowedUpdates.status !== SignalStatus.CONFIRMED_VULNERABILITY
      ? {
          ...allowedUpdates,
          confirmedVulnerabilityFindingState: null,
        }
      : allowedUpdates;

  if (signal.origin === SignalOriginSchema.enum.FINDING_CHAINING) {
    const merged: ChainingGroupedSignal = {
      ...signal,
      ...normalizedUpdates,
      origin: signal.origin,
    };

    return merged;
  }

  const merged: NonChainingGroupedSignal = {
    ...signal,
    ...normalizedUpdates,
    origin: signal.origin,
  };

  return merged;
};

const signalMatchesLifecycleFilters = ({
  signal,
  filters,
}: {
  signal: GroupedSignal;
  filters: SignalFilters;
}): boolean => {
  if (filters.stage !== 'ALL' && !filters.stage.includes(signal.stage)) {
    return false;
  }

  const statusFilter = filters.status === 'ALL' ? undefined : filters.status;
  const blockedReasonFilter = filters.blockedReason?.length
    ? filters.blockedReason
    : undefined;
  const confirmedVulnerabilityFindingStateFilter = filters
    .confirmedVulnerabilityFindingState?.length
    ? filters.confirmedVulnerabilityFindingState
    : undefined;

  if (
    !statusFilter &&
    !confirmedVulnerabilityFindingStateFilter &&
    !blockedReasonFilter
  ) {
    return true;
  }

  const matchesBlockedReason =
    blockedReasonFilter !== undefined &&
    signal.status === SignalStatus.BLOCKED &&
    signal.blockedReason !== null &&
    blockedReasonFilter.includes(signal.blockedReason);

  const matchesStatus =
    statusFilter !== undefined &&
    signal.status !== null &&
    (signal.status === SignalStatus.CONFIRMED_VULNERABILITY
      ? signal.confirmedVulnerabilityFindingState === null &&
        statusFilter.includes(signal.status)
      : statusFilter.includes(signal.status));

  const matchesConfirmedVulnerabilityFindingState =
    confirmedVulnerabilityFindingStateFilter !== undefined &&
    signal.confirmedVulnerabilityFindingState !== null &&
    confirmedVulnerabilityFindingStateFilter.includes(
      signal.confirmedVulnerabilityFindingState,
    );

  return (
    matchesStatus ||
    matchesConfirmedVulnerabilityFindingState ||
    matchesBlockedReason
  );
};

export type ConfidenceLevel = 'HIGHEST' | 'HIGH' | 'MEDIUM' | 'LOW' | 'LOWEST';

const SIGNALS_LIST_SOURCE_KEY_INDEXES = [1, 2, 3, 4, 5, 12] as const;

const isSameSignalsListSource = ({
  previousQueryKey,
  queryKey,
}: {
  previousQueryKey: readonly unknown[] | undefined;
  queryKey: readonly unknown[];
}): boolean =>
  previousQueryKey !== undefined &&
  SIGNALS_LIST_SOURCE_KEY_INDEXES.every(
    (index) => previousQueryKey[index] === queryKey[index],
  );

const SIGNALS_PER_PAGE = 50;

export const SIGNAL_SEARCH_DEBOUNCE_MS = 1000;

export type SignalFilters = {
  stage: 'ALL' | SignalStageType[];
  status: 'ALL' | SignalStatusType[];
  confirmedVulnerabilityFindingState?: ConfirmedVulnerabilityFindingStateType[];
  blockedReason?: BlockedReasonType[];
  description: string;
  confidence: ConfidenceLevel[];
  category: 'ALL' | SignalCategoryType | SignalCategoryType[];
  origin: 'ALL' | SignalOriginType[];
  source?: SignalsSearchQueryDto['source'];
  assigneeId: string[];
  url: string;
  hasExecution?: boolean;
  hasFinding?: boolean;
  hasSecurityTestCase?: boolean;
  isAppWide?: boolean;
  createdStartDate?: Date | null;
  createdEndDate?: Date | null;
  testedStartDate?: Date | null;
  testedEndDate?: Date | null;
  orderBy?: Array<{ field: string; order: 'asc' | 'desc' }>;
  groupBy?: SignalListGroupByField;
  lifecycleState?: LifecycleStateFilterDto;
};

type UseSignalsOptions = {
  targetId?: string;
  targetType?: TargetTypeType;
  endpointId?: string;
  surfaceAssetEntityId?: string;
  surfaceAssetKind?: string;
  filters: SignalFilters;
  endpointIds?: string[];
  assetIds?: string[];
  componentTypes?: AiComponentTypeType[];
  urlPatterns?: string[];
  includeApplicationLevelGroup?: boolean;
  rejectedOnly?: boolean;
  enabled?: boolean;
};

type SignalsPage = {
  signals: GroupedSignal[];
  totalCount: number;
  fetchedCount: number;
  groups?: SignalListGroupCount[];
  groupBy?: SignalListGroupByField;
};

export function useSignals({
  targetId,
  targetType,
  endpointId,
  surfaceAssetEntityId,
  surfaceAssetKind,
  filters,
  endpointIds,
  assetIds,
  componentTypes,
  urlPatterns,
  includeApplicationLevelGroup,
  rejectedOnly = false,
  enabled = true,
}: UseSignalsOptions) {
  const queryClient = useQueryClient();

  const [debouncedDescription] = useDebounceValue(
    filters.description,
    SIGNAL_SEARCH_DEBOUNCE_MS,
  );
  const [debouncedUrl] = useDebounceValue(
    filters.url,
    SIGNAL_SEARCH_DEBOUNCE_MS,
  );

  const effectiveEndpointIds = useMemo(
    () => (endpointId ? [endpointId] : endpointIds),
    [endpointId, endpointIds],
  );

  const effectiveFilters = useMemo(
    () => ({
      ...filters,
      description: debouncedDescription,
      url: debouncedUrl,
    }),
    [filters, debouncedDescription, debouncedUrl],
  );

  const buildFilters = useCallback(
    () =>
      buildSignalsRequestFilters({ filters: effectiveFilters, rejectedOnly }),
    [effectiveFilters, rejectedOnly],
  );

  const hasEmptyCategoryFilter =
    !rejectedOnly &&
    Array.isArray(effectiveFilters.category) &&
    effectiveFilters.category.length === 0;

  const queryKey = useMemo(
    () => [
      'endpoint-signals-paginated',
      targetId,
      targetType,
      endpointId,
      surfaceAssetEntityId,
      surfaceAssetKind,
      effectiveFilters,
      effectiveEndpointIds,
      assetIds,
      componentTypes,
      urlPatterns,
      includeApplicationLevelGroup,
      rejectedOnly,
    ],
    [
      targetId,
      targetType,
      endpointId,
      surfaceAssetEntityId,
      surfaceAssetKind,
      effectiveFilters,
      effectiveEndpointIds,
      assetIds,
      componentTypes,
      urlPatterns,
      includeApplicationLevelGroup,
      rejectedOnly,
    ],
  );

  const query = useInfiniteQuery({
    queryKey,
    queryFn: async ({ pageParam = 0 }): Promise<SignalsPage> => {
      if (!targetId || !targetType || hasEmptyCategoryFilter) {
        return { signals: [], totalCount: 0, fetchedCount: 0 };
      }

      const skip = pageParam;
      const builtFilters = buildFilters();

      const result = await terraProxyApiClient.listSignalsByTarget({
        targetId,
        targetType,
        skip,
        limit: SIGNALS_PER_PAGE,
        orderBy: effectiveFilters.orderBy,
        groupBy: effectiveFilters.groupBy,
        filters: builtFilters,
        endpointIds: effectiveEndpointIds,
        assetIds,
        componentTypes,
        urlPatterns,
        includeApplicationLevelGroup,
        surfaceAssetEntityId,
        surfaceAssetKind,
      });

      return {
        signals: result.signals,
        totalCount: result.totalCount,
        fetchedCount: result.signals.length,
        groups: result.groups,
        groupBy: effectiveFilters.groupBy,
      };
    },
    getNextPageParam: (lastPage, allPages) => {
      const totalFetched = allPages.reduce(
        (sum, page) => sum + page.fetchedCount,
        0,
      );

      return lastPage.fetchedCount > 0 && totalFetched < lastPage.totalCount
        ? totalFetched
        : undefined;
    },
    enabled: !!targetId && !!targetType && enabled,
    refetchOnWindowFocus: false,
    initialPageParam: 0,
    placeholderData: (previousData, previousQuery) =>
      isSameSignalsListSource({
        previousQueryKey: previousQuery?.queryKey,
        queryKey,
      })
        ? keepPreviousData(previousData)
        : undefined,
  });

  const signals = useMemo(
    () => query.data?.pages.flatMap((page) => page.signals) ?? [],
    [query.data],
  );

  const totalCount = query.data?.pages[0]?.totalCount ?? 0;
  const latestPage = query.data?.pages.at(-1);
  const groups =
    latestPage && latestPage.groupBy === effectiveFilters.groupBy
      ? latestPage.groups
      : undefined;

  const { fetchNextPage, isFetchingNextPage, hasNextPage, isPlaceholderData } =
    query;

  const loadMore = useCallback(() => {
    if (isPlaceholderData || isFetchingNextPage) {
      return;
    }

    void fetchNextPage();
  }, [fetchNextPage, isFetchingNextPage, isPlaceholderData]);

  const fetchAllSignals = useCallback(async (): Promise<GroupedSignal[]> => {
    if (!targetId || !targetType || hasEmptyCategoryFilter) {
      return [];
    }

    const allSignals: GroupedSignal[] = [];
    const signalIds = new Set<string>();
    const limit = PaginationDefaults.MAX_LIMIT;
    let skip = 0;
    let hasMore = true;

    while (hasMore) {
      const result = await terraProxyApiClient.listSignalsByTarget({
        targetId,
        targetType,
        skip,
        limit,
        orderBy: effectiveFilters.orderBy,
        filters: buildFilters(),
        endpointIds: effectiveEndpointIds,
        assetIds,
        componentTypes,
        urlPatterns,
        includeApplicationLevelGroup,
        surfaceAssetEntityId,
        surfaceAssetKind,
      });
      const pageSignals = result.signals;

      const loadedBeforePage = allSignals.length;

      pageSignals.forEach((signal) => {
        if (signalIds.has(signal.id)) {
          return;
        }

        signalIds.add(signal.id);
        allSignals.push(signal);
      });

      hasMore =
        allSignals.length > loadedBeforePage && pageSignals.length === limit;
      skip += limit;
    }

    return allSignals;
  }, [
    targetId,
    targetType,
    hasEmptyCategoryFilter,
    effectiveFilters.orderBy,
    buildFilters,
    effectiveEndpointIds,
    assetIds,
    componentTypes,
    urlPatterns,
    includeApplicationLevelGroup,
    surfaceAssetEntityId,
    surfaceAssetKind,
  ]);

  const updateSignal = useCallback(
    (signalId: string, updates: Partial<BaseAppSignalDto>) => {
      queryClient.setQueryData<InfiniteData<SignalsPage, number>>(
        queryKey,
        (oldData) => {
          if (!oldData) return oldData;

          return {
            ...oldData,
            pages: oldData.pages.map((page) => ({
              ...page,
              signals: page.signals
                .map((signal) =>
                  signal.id === signalId
                    ? mergeGroupedSignalUpdate(signal, updates)
                    : signal,
                )
                .filter((signal) =>
                  signalMatchesLifecycleFilters({
                    signal,
                    filters: effectiveFilters,
                  }),
                ),
            })),
          };
        },
      );
    },
    [queryClient, queryKey, effectiveFilters],
  );

  const removeSignals = useCallback(
    (signalIds: string[]) => {
      const signalIdsSet = new Set(signalIds);

      queryClient.setQueryData<InfiniteData<SignalsPage, number>>(
        queryKey,
        (oldData) => {
          if (!oldData) return oldData;

          return {
            ...oldData,
            pages: oldData.pages.map((page) => ({
              ...page,
              totalCount: Math.max(page.totalCount - signalIdsSet.size, 0),
              signals: page.signals.filter(
                (signal) => !signalIdsSet.has(signal.id),
              ),
            })),
          };
        },
      );
    },
    [queryClient, queryKey],
  );

  return {
    signals,
    totalCount,
    groups,
    isLoading: query.isLoading,
    isLoadingMore: isFetchingNextPage,
    hasNextPage: hasNextPage ?? false,
    error: query.error,
    refetch: query.refetch,
    loadMore,
    updateSignal,
    removeSignals,
    fetchAllSignals,
    isFetching: query.isFetching,
  };
}
